import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { queryParams } from '../constants/routing';
import { updateClaimLinkBuilderField } from '../store/actions/templates';
import { selectCurrentOrganizationId } from '../store/selectors/organizations';
import { TTemplateMediaFieldName } from '../types/templates';
import { UploadFileTypeEnum } from '../types/upload';
import notification from '../utils/notification';
import { getQuery } from '../utils/request';
import useUploadFile from './useUploadFile';

type OnSuccessCallback = (fieldName: TTemplateMediaFieldName, value: string | null) => void;

interface IProps {
  onSuccess?: OnSuccessCallback;
}

export const useTemplateMediaUpload = ({ onSuccess }: IProps = {}) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectCurrentOrganizationId);
  const { uploadFile } = useUploadFile();

  const defaultOnSuccess = useCallback<OnSuccessCallback>(
    (fieldName, value) => {
      dispatch(updateClaimLinkBuilderField({ [fieldName]: value }));
    },
    [dispatch],
  );

  const handleSuccess = useMemo(() => onSuccess || defaultOnSuccess, [onSuccess, defaultOnSuccess]);

  const handleMediaUpload = useCallback(
    (fieldName: TTemplateMediaFieldName) => async (acceptedFiles: File[]) => {
      if (!acceptedFiles[0]) {
        return;
      }

      try {
        const response = await uploadFile(
          acceptedFiles[0],
          UploadFileTypeEnum.TemplateMedia,
          getQuery(orgId, queryParams.orderId),
        );
        handleSuccess(fieldName, response.public_url);
      } catch (error: any) {
        notification.error('Upload failed', { content: error?.message || 'Unknown error' });
      }
    },
    [uploadFile, orgId, handleSuccess],
  );

  const handleMediaRemove = useCallback(
    (fieldName: TTemplateMediaFieldName) => () => {
      handleSuccess(fieldName, '');
    },
    [handleSuccess],
  );

  return { handleMediaUpload, handleMediaRemove };
};
